import { useState } from 'react';

import { getDateFormat, getFormattedDate, showToast } from '../../../helper';
import API, { ClickhouseAPI, CLICKHOUSE_REPORTS, TravelAPI } from '../../../helper/API';
import { getOrganizationData } from '../../../helper/organizationStorage';
import { DATE_FORMAT_BE, OA_DATE_FORMAT_BE } from '../../../constants';
import { IFlightProvider, IFlightQueryParams } from '../type';
import FlightContext, { IFlightContext, ReportType } from './FlightContext';
import { IPageInfo } from '../../../components/OATableComponent';
import FileDownload from 'js-file-download';
import { flightFilterNavs } from '../../order-management/constants';

const FlightProvider = (props: IFlightProvider) => {
  const { queryParams } = props?.data;
  const [statusChange, setStatusChange] = useState<boolean>(false);
  const [report, setReport] = useState<{ isLoading: boolean; activeReport: ReportType | null }>({ isLoading: false, activeReport: null });
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    totalCount: 0,
    totalPages: 0,
    count: 0,
    size: 10,
    index: 1
  });
  const getFlightOrderList = async (cb?: (response?: any) => void) => {
    try {
      API.get(`/flight/order/dashboard?${buildQueryParams(queryParams)}`)
        .then((response: any) => {
          const resultData = response?.data?.data;
          const contentLength = resultData?.content?.length ?? 0;
          const currentPage = queryParams?.pageIndex ?? 1;
          const PAGE_SIZE = 10;

          let computedTotalCount: number;
          let computedTotalPages: number;

          if (contentLength === PAGE_SIZE) {
            // Full page — assume at least one more page exists
            computedTotalPages = currentPage + 1;
            computedTotalCount = currentPage * PAGE_SIZE + 1;
          } else if (contentLength > 0) {
            // Partial page — this is the last page
            computedTotalPages = currentPage;
            computedTotalCount = (currentPage - 1) * PAGE_SIZE + contentLength;
          } else {
            // Empty — we've gone past the last page
            computedTotalPages = Math.max(currentPage - 1, 0);
            computedTotalCount = Math.max((currentPage - 1) * PAGE_SIZE, 0);
          }

          setPageInfo(prev =>
            Object.assign({}, prev, {
              totalCount: computedTotalCount,
              totalPages: computedTotalPages,
              count: contentLength
            })
          );
          cb?.(resultData);
        })
        .catch(err => {
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const getOrderById = async (id: string, cb?: (response?: any) => void) => {
    try {
      API.get(`/flight/order/${id}`)
        .then((response: any) => {
          const resultData = response?.data?.data;
          // showToast('Order fetched successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const getCancelReasons = async (id: string, cb?: (response?: any) => void) => {
    try {
      API.get(`/flight/order/cancel/info/${id}`)
        .then((response: any) => {
          const resultData = response?.data?.data;
          // showToast('Order fetched successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const cancelFlightsBooking = async (
    id: string,
    requestbody: any,
    cb?: (response?: any) => void
  ) => {
    setStatusChange(false);
    try {
      API.post(`/flight/order/cancel/${id}`, requestbody)
        .then((response: any) => {
          setStatusChange(true);
          const resultData = response?.data?.data;
          showToast('Flight cancelled successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          setStatusChange(false);
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const updatePNR = async (draft: any, queryDraft: any, cb?: (response?: any) => void) => {
    setStatusChange(false);
    try {
      API.put(`/flight/order/update/${queryDraft?.id}?flightTripId=${queryDraft?.tripId}&segmentId=${queryDraft?.segmentId}`, draft)
        .then((response: any) => {
          setStatusChange(true);
          const resultData = response?.data?.data;
          showToast('PNR updated successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          setStatusChange(false);
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const updateStatus = async (draft: any, cb?: (response?: any) => void) => {
    setStatusChange(false);
    try {
      API.put(`/flight/order/update/${draft?.id}?status=${draft?.status}&message=${draft?.message}`)
        .then((response: any) => {
          setStatusChange(true);
          const resultData = response?.data?.data;
          showToast('Status updated successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          setStatusChange(false);
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const downloadReport = (reportType: ReportType = 'internal', email?: string, fromDate?: string, toDate?: string) => {
    const startDate = getFormattedDate(queryParams?.dateRange?.[0], OA_DATE_FORMAT_BE);
    const endDate = getFormattedDate(queryParams?.dateRange?.[1], OA_DATE_FORMAT_BE);
    const orgData = getOrganizationData();
    const orgCode = orgData?.organizationCode;

    const buildClickhouseParams = (endpoint: string) =>
      `${endpoint}?format=CSVWithNames&param_org_code=${orgCode}&param_start_date=${startDate}&param_end_date=${endDate}`;

    const reportConfigs: Record<ReportType, { url: string; filename: string; api: 'default' | 'clickhouse' | 'travel' }> = {
      internal: {
        url: `/flight/order/report?${buildQueryParamsReport(queryParams)}`,
        filename: 'flight_report.csv',
        api: 'default'
      },
      booking_analytics: {
        url: buildClickhouseParams(CLICKHOUSE_REPORTS.BOOKING_ANALYTICS),
        filename: `flight_booking_analytics_${startDate}_to_${endDate}.csv`,
        api: 'clickhouse'
      },
      cancellation: {
        url: buildClickhouseParams(CLICKHOUSE_REPORTS.CANCELLATION),
        filename: `cancellation_report_${startDate}_to_${endDate}.csv`,
        api: 'clickhouse'
      },
      email: {
        url: `/v2/flight/orders/report/async?start=${fromDate}&end=${toDate}&email=${email}`,
        filename: 'flight_report.csv',
        api: 'travel'
      }
    };

    const config = reportConfigs[reportType];

    try {
      setReport({ isLoading: true, activeReport: reportType });

      if (config.api === 'clickhouse') {
        ClickhouseAPI.get(config.url)
          .then((response: any) => {
            showToast('Report generated!', 'success');
            setReport({ isLoading: false, activeReport: null });
            FileDownload(response?.data, config.filename);
          })
          .catch((err: any) => {
            setReport({ isLoading: false, activeReport: null });
            showToast(err?.message || 'Failed to download report', 'error');
          });
      } else if (config.api === 'travel') {
        TravelAPI.get(config.url)
          .then((response: any) => {
            showToast('Report sent to email successfully!', 'success');
            setReport({ isLoading: false, activeReport: null });
          })
          .catch((err: any) => {
            setReport({ isLoading: false, activeReport: null });
            showToast(err?.message || 'Failed to send email report', 'error');
          });
      } else {
        const APITemp: any = API;
        APITemp.responseType = 'blob';
        APITemp.get(config.url)
          .then((response: any) => {
            showToast('Report generated!', 'success');
            setReport({ isLoading: false, activeReport: null });
            FileDownload(response?.data, config.filename);
          })
          .catch((err: any) => {
            setReport({ isLoading: false, activeReport: null });
            showToast(err?.message, 'error');
          });
      }
    } catch (error: any) {
      setReport({ isLoading: false, activeReport: null });
      showToast(error?.message || 'Failed to download report', 'error');
    }
  };

  const downloadInvoice = async (orderID: any, cb?: (response?: any) => void) => {
    try {
      API.get(`/flight/order/invoice/${orderID}`)
        .then((response: any) => {
          const resultData = response?.data?.data;
          showToast('Invoice downloaded successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const downloadEticket = async (orderID: any, cb?: (response?: any) => void) => {
    try {
      API.get(`/flight/order/attachment/${orderID}`)
        .then((response: any) => {
          const resultData = response?.data?.data;
          showToast('Invoice downloaded successfully', 'success');
          cb?.(resultData);
        })
        .catch(err => {
          showToast(err?.message, 'error');
          cb?.(err);
          throw new Error(`API error:${err?.message}`);
        });
    } catch (error: any) {
      showToast(error?.message, 'error');
      cb?.(error);
      throw new Error(`API error: ${error?.message}`);
    }
  };

  const contextValue: IFlightContext = {
    getFlightOrderList,
    getOrderById,
    getCancelReasons,
    cancelFlightsBooking,
    updatePNR,
    updateStatus,
    downloadReport,
    downloadInvoice,
    downloadEticket,
    report,
    pageInfo,
    statusChange
  };

  return <FlightContext.Provider value={contextValue}>{props?.children}</FlightContext.Provider>;
};

export default FlightProvider;

const buildQueryParams = (queryParams: IFlightQueryParams): string => {
  let queryStr = `page=${queryParams?.pageIndex - 1}&size=10`;
  if (queryParams?.search && queryParams?.search !== '') {
    queryStr = `${queryStr}&search=${queryParams?.search}`;
  }
  if (queryParams?.dateRange.length > 0) {
    const startDate = queryParams?.dateRange[0]
      ? getFormattedDate(queryParams?.dateRange[0], OA_DATE_FORMAT_BE)
      : null;
    const endDate = queryParams?.dateRange[1]
      ? getFormattedDate(queryParams?.dateRange[1], OA_DATE_FORMAT_BE)
      : null;
    queryStr = `${queryStr}&start=${startDate}&end=${endDate}`;
  }

  if (queryParams?.sortBy && queryParams?.sortByField) {
    queryStr = `${queryStr}&sortByField=${queryParams?.sortByField}&sortBy=${queryParams?.sortBy}`;
  }
  if (queryParams?.orderStatus && queryParams?.orderStatus !== '') {
    let ifFound = flightFilterNavs?.find((obj: any) => obj === queryParams?.orderStatus);
    queryStr = ifFound
      ? queryParams?.orderStatus === 'ORDER_DRAFT' || queryParams?.orderStatus === ''
        ? queryStr
        : `${queryStr}&status=${queryParams?.orderStatus}`
      : `${queryStr}`;
  }
  return queryStr;
};

const buildQueryParamsReport = (queryParams: IFlightQueryParams): string => {
  const startDate = getDateFormat(queryParams?.dateRange?.[0] as Date, DATE_FORMAT_BE);
  const endDate = getDateFormat(queryParams?.dateRange?.[1] as Date, DATE_FORMAT_BE);
  let queryStr = `start=${startDate}&end=${endDate}`;
  return queryStr;
};
